/**
 * Herkunftskontext für die Werbemessung (Ismail 18.09.2026, Punkt 2 + 4):
 * `ia_src` (Kennzeichen der IA-Kampagnen-URLs) und Google-Click-IDs werden
 * beim Landen aus der URL in ein First-Party-Cookie auf `.isg-express.com`
 * übernommen, damit sie den Wechsel www → app und die Sprachseiten
 * überleben. `ia_src` wird zusätzlich in den dataLayer gelegt, weil der
 * GTM-Trigger „purchase und ia_src=1“ die Variable auf der App-Seite braucht.
 * Keine künstlichen IDs: nur, was die URL tatsächlich mitbrachte.
 */
import { marketingConsentGranted } from "./openai-ads";

const COOKIE = "isg_ads_ctx";
const KEYS = ["ia_src", "gclid", "gbraid", "wbraid"] as const;
/** Click-IDs sind pseudonyme Kennungen → nur mit Marketing-Einwilligung speichern
 *  (Reviewer 18.09.2026). `ia_src` ist eine reine Kampagnenkennung ohne Personenbezug. */
const CONSENT_KEYS: ReadonlySet<string> = new Set(["gclid", "gbraid", "wbraid"]);
type Key = (typeof KEYS)[number];
export type AdsContext = Partial<Record<Key, string>>;

function cookieDomain(): string {
  return /(^|\.)isg-express\.com$/.test(location.hostname) ? "; domain=.isg-express.com" : "";
}

export function readAdsContext(): AdsContext {
  if (typeof document === "undefined") return {};
  const m = document.cookie.match(new RegExp(`(?:^|; )${COOKIE}=([^;]*)`));
  if (!m) return {};
  try {
    const parsed = JSON.parse(decodeURIComponent(m[1])) as AdsContext;
    const out: AdsContext = {};
    for (const k of KEYS) if (typeof parsed[k] === "string" && parsed[k]) out[k] = parsed[k]!.slice(0, 200);
    return out;
  } catch {
    return {};
  }
}

/** Beim Laden: URL-Parameter übernehmen, Cookie 30 Tage, `ia_src` in den dataLayer. */
export function captureAdsContext(): AdsContext {
  if (typeof window === "undefined") return {};
  const params = new URLSearchParams(window.location.search);
  const ctx = readAdsContext();
  let changed = false;
  const consent = marketingConsentGranted();
  for (const k of KEYS) {
    if (CONSENT_KEYS.has(k) && !consent) continue;
    // Die Google-DACH-Kampagnen (Ismail, 18.09.2026) senden `iasrc=1` ohne
    // Unterstrich; beide Schreibweisen führen zum Schlüssel `ia_src`.
    const v = params.get(k) ?? (k === "ia_src" ? params.get("iasrc") : null);
    if (v && /^[A-Za-z0-9_.-]{1,200}$/.test(v) && ctx[k] !== v) {
      ctx[k] = v;
      changed = true;
    }
  }
  if (changed) {
    try {
      document.cookie = `${COOKIE}=${encodeURIComponent(JSON.stringify(ctx))}; path=/; max-age=${30 * 86400}; samesite=lax${cookieDomain()}`;
    } catch {
      /* Cookie-Fehler dürfen nichts blockieren */
    }
  }
  try {
    if (ctx.ia_src) {
      const w = window as unknown as { dataLayer?: unknown[] };
      w.dataLayer = w.dataLayer ?? [];
      w.dataLayer.push({ ia_src: ctx.ia_src });
    }
  } catch {
    /* s. o. */
  }
  return ctx;
}

/** Widerruf der Marketing-Einwilligung (Reviewer 18.09.2026): gespeicherte
 *  Click-IDs entfernen, die Kampagnenkennung `ia_src` bleibt. */
export function dropClickIds(): void {
  if (typeof document === "undefined") return;
  const ctx = readAdsContext();
  if (!ctx.gclid && !ctx.gbraid && !ctx.wbraid) return;
  const kept: AdsContext = ctx.ia_src ? { ia_src: ctx.ia_src } : {};
  try {
    document.cookie = `${COOKIE}=${encodeURIComponent(JSON.stringify(kept))}; path=/; max-age=${30 * 86400}; samesite=lax${cookieDomain()}`;
  } catch {
    /* s. o. */
  }
}

/** Für Server-Nachweise: nur Vorhandensein, keine Click-IDs. */
export function adsContextPresence(ctx: AdsContext): { iaSrc: boolean; gclid: boolean } {
  return { iaSrc: Boolean(ctx.ia_src), gclid: Boolean(ctx.gclid || ctx.gbraid || ctx.wbraid) };
}
